/**
 * Нагрузка на API: RPS, время ответа и ошибки — в целом и по ручкам.
 */
import { useMemo, useState } from "react";
import { Alert, Button, Card, Col, Row, Segmented, Space, Spin, Statistic, Table, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { ChartCard } from "./chart";
import { COLORS, axisProps, fmt, gridProps, percent, tooltipProps } from "./chartTheme";
import { type RequestMetrics, type RequestStats, num } from "./types";
import { useMetrics } from "./useMetrics";

const WINDOWS = [
  { label: "15 минут", value: 15 },
  { label: "Час", value: 60 },
  { label: "6 часов", value: 360 },
  { label: "Сутки", value: 1440 },
  { label: "Неделя", value: 10080 },
  { label: "30 дней", value: 43200 },
];

/** /chronoguessr.v1.QuestService/ListQuests → QuestService · ListQuests */
function shortMethod(method: string): { service: string; name: string } {
  const [, service = "", name = method] = method.match(/\.([A-Za-z]+Service)\/(.+)$/) ?? [];
  return { service, name };
}

function ms(value: number): string {
  if (value >= 1000) return `${(value / 1000).toLocaleString("ru-RU", { maximumFractionDigits: 2 })} с`;
  return `${value.toLocaleString("ru-RU", { maximumFractionDigits: 1 })} мс`;
}

function rps(value: number): string {
  // Редкую ручку честнее показать «меньше 0,001», чем нулём, будто её не звали.
  if (value > 0 && value < 0.001) return "< 0,001";
  return value.toLocaleString("ru-RU", { maximumFractionDigits: value < 1 ? 3 : 1 });
}

export function LoadTab() {
  const [minutes, setMinutes] = useState(60);
  const [method, setMethod] = useState<string | undefined>();

  // Конец окна сервер берёт «сейчас», начало считается при каждом запросе.
  const { data, error, loading, reload } = useMetrics<RequestMetrics>(
    "/api/v1/admin/metrics/requests",
    () => ({ from: dayjs().subtract(minutes, "minute").toISOString(), method }),
    [minutes, method],
    30_000,
  );

  const timeFormat = minutes <= 1440 ? "HH:mm" : "D MMM HH:mm";
  const series = useMemo(
    () =>
      (data?.series ?? []).map((p) => ({
        time: dayjs(p.time).format(timeFormat),
        rps: p.rps,
        avg: p.avgMs,
        p95: p.p95Ms,
        errors: num(p.serverErrors),
      })),
    [data, timeFormat],
  );

  const total = data?.total;
  const title = method ? `${shortMethod(method).service} · ${shortMethod(method).name}` : "все ручки";

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Space wrap>
        <Segmented options={WINDOWS} value={minutes} onChange={(v) => setMinutes(v as number)} />
        <Button onClick={() => void reload()}>Обновить</Button>
        {loading && <Spin size="small" />}
        <Typography.Text type="secondary">Обновляется раз в 30 секунд</Typography.Text>
      </Space>

      {error && <Alert type="error" showIcon message="Метрики не загрузились" description={error} />}

      {total && <Totals stats={total} />}

      {method && (
        <Alert
          type="info"
          showIcon
          message={<>Графики — по ручке <b>{title}</b>. Таблица ниже — по всем.</>}
          action={<Button size="small" onClick={() => setMethod(undefined)}>Показать все</Button>}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <ChartCard title={`Запросов в секунду — ${title}`} hint={data ? `шаг ${stepLabel(data.stepSeconds)}` : undefined}>
            <AreaChart data={series}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="time" {...axisProps} minTickGap={24} />
              <YAxis {...axisProps} width={48} />
              <ChartTooltip {...tooltipProps} formatter={(v: number) => rps(v)} />
              <Area type="monotone" dataKey="rps" name="RPS" stroke={COLORS.amber} fill={COLORS.amber} fillOpacity={0.25} isAnimationActive={false} />
            </AreaChart>
          </ChartCard>
        </Col>
        <Col xs={24} xl={12}>
          <ChartCard title={`Время ответа — ${title}`}>
            <LineChart data={series}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="time" {...axisProps} minTickGap={24} />
              <YAxis {...axisProps} width={56} unit=" мс" />
              <ChartTooltip {...tooltipProps} formatter={(v: number) => ms(v)} />
              <Legend />
              <Line type="monotone" dataKey="avg" name="Среднее" stroke={COLORS.blue} dot={false} strokeWidth={2} isAnimationActive={false} />
              <Line type="monotone" dataKey="p95" name="p95" stroke={COLORS.violet} dot={false} strokeWidth={2} isAnimationActive={false} />
            </LineChart>
          </ChartCard>
        </Col>
        <Col xs={24}>
          <ChartCard title={`Ошибки сервиса — ${title}`} height={160}>
            <BarChart data={series}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="time" {...axisProps} minTickGap={24} />
              <YAxis allowDecimals={false} {...axisProps} width={40} />
              <ChartTooltip {...tooltipProps} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Bar dataKey="errors" name="Ошибок" fill={COLORS.red} isAnimationActive={false} />
            </BarChart>
          </ChartCard>
        </Col>
      </Row>

      <Card size="small" title="Ручки за период" extra={<Typography.Text type="secondary">Щёлкните строку, чтобы построить графики по ней</Typography.Text>}>
        <Table
          size="small"
          rowKey="method"
          dataSource={data?.methods ?? []}
          pagination={{ pageSize: 25, hideOnSinglePage: true }}
          onRow={(row) => ({
            onClick: () => setMethod(row.method === method ? undefined : row.method),
            style: { cursor: "pointer", background: row.method === method ? "rgba(217,148,71,0.08)" : undefined },
          })}
          locale={{ emptyText: "За период запросов не было" }}
          columns={[
            {
              title: "Ручка",
              dataIndex: "method",
              render: (m: string) => {
                const { service, name } = shortMethod(m);
                return (
                  <Tooltip title={m}>
                    <Typography.Text type="secondary">{service} · </Typography.Text>
                    <Typography.Text>{name}</Typography.Text>
                  </Tooltip>
                );
              },
            },
            {
              title: "Запросов",
              align: "right",
              sorter: (a, b) => num(a.stats.count) - num(b.stats.count),
              defaultSortOrder: "descend",
              render: (_, r) => fmt(num(r.stats.count)),
            },
            { title: "RPS", align: "right", sorter: (a, b) => a.stats.rps - b.stats.rps, render: (_, r) => rps(r.stats.rps) },
            { title: "Среднее", align: "right", sorter: (a, b) => a.stats.avgMs - b.stats.avgMs, render: (_, r) => ms(r.stats.avgMs) },
            { title: "p50", align: "right", sorter: (a, b) => a.stats.p50Ms - b.stats.p50Ms, render: (_, r) => ms(r.stats.p50Ms) },
            { title: "p95", align: "right", sorter: (a, b) => a.stats.p95Ms - b.stats.p95Ms, render: (_, r) => ms(r.stats.p95Ms) },
            { title: "p99", align: "right", sorter: (a, b) => a.stats.p99Ms - b.stats.p99Ms, render: (_, r) => ms(r.stats.p99Ms) },
            { title: "Макс.", align: "right", sorter: (a, b) => a.stats.maxMs - b.stats.maxMs, render: (_, r) => ms(r.stats.maxMs) },
            {
              title: "Ошибки",
              align: "right",
              sorter: (a, b) => num(a.stats.serverErrors) - num(b.stats.serverErrors),
              render: (_, r) => <ErrorTags stats={r.stats} />,
            },
          ]}
        />
      </Card>
    </Space>
  );
}

function Totals({ stats }: { stats: RequestStats }) {
  const count = num(stats.count);
  return (
    <Row gutter={[16, 16]}>
      <Col xs={12} md={6} xl={4}>
        <Card size="small"><Statistic title="Запросов" value={fmt(count)} /></Card>
      </Col>
      <Col xs={12} md={6} xl={4}>
        <Card size="small"><Statistic title="Средний RPS" value={rps(stats.rps)} /></Card>
      </Col>
      <Col xs={12} md={6} xl={4}>
        <Card size="small"><Statistic title="Среднее время" value={ms(stats.avgMs)} /></Card>
      </Col>
      <Col xs={12} md={6} xl={4}>
        <Card size="small">
          <Tooltip title="Медиана / 95-й / 99-й перцентиль: столько ждали половина, 95% и 99% запросов">
            <Statistic title="p50 / p95 / p99" value={`${ms(stats.p50Ms)} · ${ms(stats.p95Ms)} · ${ms(stats.p99Ms)}`} valueStyle={{ fontSize: 18 }} />
          </Tooltip>
        </Card>
      </Col>
      <Col xs={12} md={6} xl={4}>
        <Card size="small">
          <Tooltip title="Сбои сервиса: Internal, Unavailable, таймауты">
            <Statistic
              title="Ошибки сервиса"
              value={`${fmt(num(stats.serverErrors))} · ${percent(num(stats.serverErrors), count)}`}
              valueStyle={num(stats.serverErrors) ? { color: COLORS.red } : undefined}
            />
          </Tooltip>
        </Card>
      </Col>
      <Col xs={12} md={6} xl={4}>
        <Card size="small">
          <Tooltip title="Отказы клиенту: неверный ввод, нет входа или прав, не найдено. Обычная работа валидации">
            <Statistic title="Отказы клиенту" value={`${fmt(num(stats.clientErrors))} · ${percent(num(stats.clientErrors), count)}`} />
          </Tooltip>
        </Card>
      </Col>
    </Row>
  );
}

function ErrorTags({ stats }: { stats: RequestStats }) {
  const server = num(stats.serverErrors);
  const client = num(stats.clientErrors);
  return (
    <Space size={4}>
      {server > 0 && <Tooltip title="Ошибки сервиса"><Tag color="red">{fmt(server)}</Tag></Tooltip>}
      {client > 0 && <Tooltip title="Отказы клиенту"><Tag>{fmt(client)}</Tag></Tooltip>}
      {!server && !client && <Typography.Text type="secondary">—</Typography.Text>}
    </Space>
  );
}

function stepLabel(seconds: number): string {
  if (seconds < 3600) return `${seconds / 60} мин`;
  if (seconds < 86400) return `${seconds / 3600} ч`;
  return `${seconds / 86400} сут`;
}
